import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const EMPTY = { name:'', email:'', password:'', role:'manager' };

export default function Users() {
  const [users, setUsers]   = useState([]);
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const { user: me } = useAuth();

  const load = useCallback(() => {
    axios.get('/api/users').then(r => setUsers(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setForm(EMPTY); setEditing(null); setModal(true); };
  const openEdit = u => { setForm({ name:u.name, email:u.email, password:'', role:u.role }); setEditing(u.id); setModal(true); };
  const closeModal = () => setModal(false);

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) { await axios.put(`/api/users/${editing}`, payload); toast.success('User updated'); }
      else         { await axios.post('/api/users', payload); toast.success('User created'); }
      closeModal(); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async id => {
    if (id === me?.id) return toast.error("Can't delete yourself");
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`/api/users/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const ROLE_BADGE = { superadmin:'#0057FF', admin:'#8B5CF6', manager:'#10B981' };

  return (
    <div>
      <div className="page-header">
        <div><h1>Users</h1><p>Admin panel user accounts</p></div>
        <button className="btn btn--primary" onClick={openNew}>+ Add User</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:'50%', background:`linear-gradient(135deg,#0057FF,#00D4FF)`, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0 }}>{u.name.charAt(0)}</div>
                      <strong>{u.name}</strong>
                    </div>
                  </td>
                  <td>{u.email}</td>
                  <td><span className="badge" style={{ background: ROLE_BADGE[u.role]+'20', color: ROLE_BADGE[u.role] }}>{u.role}</span></td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={{ display:'flex', gap:8 }}>
                    <button className="btn btn--ghost btn--sm" onClick={() => openEdit(u)}>Edit</button>
                    {u.id !== me?.id && <button className="btn btn--danger btn--sm" onClick={() => remove(u.id)}>Delete</button>}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5}><div className="empty-state"><div className="icon">👤</div>No users found</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <h2>{editing ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={save}>
              <div className="form-field">
                <label>Name *</label>
                <input className="form-control" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Email *</label>
                <input className="form-control" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-field">
                <label>{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input className="form-control" type="password" required={!editing} value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Role</label>
                <select className="form-control" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--ghost" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
